(ns minorodata.components.pipeline-dag.loading
  "Loading screen naming the running graph-build step, driven by `dag:phase` events."
  (:require
   ["lucide-react" :refer [Check Loader2 TriangleAlert]]
   [applied-science.js-interop :as j]
   ["react" :as react]
   [minorodata.pipeline-dag.timing :as timing]
   [ui.utils :refer [clsx]]))

(def ^:private steps
  "User-facing steps and the timing phases each covers."
  [{:label "Loading pipeline" :phases #{"fetch"}}
   {:label "Preparing graph"  :phases #{"prune" "parse" "elk-input"}}
   {:label "Arranging layout" :phases #{"elk"}}
   {:label "Drawing"          :phases #{"flatten"}}])

(defn- step-of [phase]
  (first (keep-indexed (fn [i {:keys [phases]}] (when (phases phase) i)) steps)))

(defn- loading-inner []
  ;; starts on the fetch, which may already be in flight when this mounts
  (let [[{:keys [step running?]} set-state] (react/useState {:step 0 :running? true})
        [elapsed set-elapsed] (react/useState 0)]

    ;; an event without `ms` is a start; events for earlier steps are ignored
    (react/useEffect
     (fn []
       (let [on-phase (fn [e]
                        (let [d (j/get e :detail)
                              i (step-of (j/get d :phase))]
                          (when i
                            (set-state
                             (fn [prev]
                               (if (< i (:step prev))
                                 prev
                                 {:step i :running? (nil? (j/get d :ms))}))))))]
         (js/window.addEventListener timing/event-name on-phase)
         #(js/window.removeEventListener timing/event-name on-phase)))
     #js [])

    (react/useEffect
     (fn []
       (set-elapsed 0)
       (let [t (js/setInterval #(set-elapsed inc) 1000)]
         #(js/clearInterval t)))
     #js [step])

    [:div {:class "flex items-center justify-center h-full" :data-cy "dag-loading"}
     [:div {:class "flex flex-col gap-3 min-w-64"}
      (for [[i {:keys [label]}] (map-indexed vector steps)
            :let [active? (and (= i step) running?)
                  done?   (or (< i step) (and (= i step) (not running?)))]]
        ^{:key i}
        [:div {:class (clsx "flex items-center gap-2.5 text-sm transition-colors"
                            (cond active? "text-kleene-text"
                                  done?   "text-kleene-text-secondary"
                                  :else   "text-kleene-text-secondary/50"))}
         [:span {:class "inline-flex items-center justify-center w-4 h-4 shrink-0"}
          (cond
            active? [:> Loader2 {:size 14 :class "animate-spin text-kleene-primary-v2"}]
            done?   [:> Check {:size 14 :class "text-kleene-green"}]
            :else   [:span {:class "w-1.5 h-1.5 rounded-full bg-current opacity-40"}])]
         [:span (cond-> label active? (str "…"))]
         (when (and active? (>= elapsed 3))
           [:span {:class "ml-auto tabular-nums text-xs text-kleene-text-secondary"}
            (str elapsed "s")])])]]))

(defn failed-screen
  "Shown in place of the graph when ELK could not lay it out."
  []
  [:div {:class "flex flex-col items-center gap-2 text-sm px-4 py-3 rounded-lg border shadow-md bg-kleene-background"
         :data-cy "dag-layout-failed"}
   [:div {:class "flex items-center gap-2 text-kleene-text"}
    [:> TriangleAlert {:size 16 :class "text-kleene-warning-01"}]
    "Couldn't lay out this graph"]
   [:span {:class "text-kleene-text-secondary"}
    "It may be too large. Try filtering it down to fewer groups or sources."]])

(defn loading-screen
  "Drop-in replacement for `ui.page/page-loading-screen` on the graph pages."
  []
  [:f> loading-inner])
